import { Alert, Flex, Heading, View } from "@instructure/ui";
import c3 from "c3";
import * as d3 from "d3";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { DashboardService } from "../../services/DashboardService.js";
import { default as colors } from "../../utils/ChartColors";
import { FeatureContext } from "../../utils/Context.js";
import { sort } from "../../utils/utils.js";
import Legend from "../Cards/Legend.js";

const LineChart = (props) => {
  const {
    filters,
    trigger,
    setTrigger,
    clear,
    subs,
    inAnalyze,
    showBarChart,
    setBarChartWidth,
    isFacultyDash,
    assignmentData,
    section,
    chartLoad,
    setChartLoad,
    setChartTrigger,
    customTitle,
  } = props;
  const { t } = useTranslation();
  const features = useContext(FeatureContext);
  const [graphData, setGraphData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [prevSubject, setPrevSubject] = useState(filters.subject);
  const [prevLoType, setPrevLoType] = useState(filters.LOType);
  const [chart, setChart] = useState(null);
  const [graphColors, setGraphColors] = useState({});

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  const aggregateResultsData = (assignment_results, result_type, lo_type) => {
    const agg_results = {};
    for (const outcome_n in assignment_results) {
      if (outcome_n !== "id" && outcome_n !== "assignment") {
        const outcome_level_dict = assignment_results[outcome_n];
        if (outcome_level_dict["outcome"]) {
          const outcome_name = outcome_level_dict["outcome"]["name"];
          if (outcome_level_dict["outcome"]["outcome_type"] === lo_type) {
            if (Object.hasOwn(agg_results, outcome_name)) {
              agg_results[outcome_name].push(outcome_level_dict[result_type]);
            } else {
              agg_results[outcome_name] = [outcome_level_dict[result_type]];
            }
          }
        }
      }
    }
    return agg_results;
  };

  const getAverageResultsData = (assignment_results, result_type, lo_type) => {
    const agg_results = aggregateResultsData(
      assignment_results,
      result_type,
      lo_type
    );
    const avg_results = {};
    for (const key in agg_results) {
      const sum = agg_results[key].reduce((a, b) => a + b, 0);
      const avg = sum / agg_results[key].length;
      avg_results[key] = avg;
    }
    return avg_results;
  };

  const getRowOfDataForAverageEntry = (
    lo,
    numTerms,
    indexOfTerm,
    av_results
  ) => {
    const data_array_for_lo_avg = Array(numTerms).fill(null);
    data_array_for_lo_avg[0] = t("dashboard.assignmentScore") + lo;
    data_array_for_lo_avg[indexOfTerm] = Math.round(av_results[lo] * 100) / 100;
    return data_array_for_lo_avg;
  };

  const setAverageDataColors = (roundedResults, lo, all_data_colors) => {
    const associated_whole_lo_line = roundedResults.filter(
      (row) => row[0] === lo
    );
    const associated_index =
      roundedResults.indexOf(associated_whole_lo_line[0]) - 1;
    all_data_colors[lo] = colors[associated_index * 2];
    all_data_colors[t("dashboard.assignmentScore") + lo] =
      colors[associated_index * 2 + 1];
    return all_data_colors;
  };

  function determineContext(fetchFinalized) {
    if (fetchFinalized) {
      return null;
    } else {
      if (
        !section ||
        section.canvas_id !== -1 ||
        (section.canvas_id === -1 &&
          !filters.courses.length &&
          !filters.offerings.length)
      ) {
        return "canvas_course_embedded,external_assessment,file_upload";
      } else {
        return "external_assessment";
      }
    }
  }

  const lineChartUpdate = () => {
    if (!dataLoaded) {
      if (!filters.subject && isFacultyDash) {
        filters.subject = t("dashboard.noDepartmentFound");
      } else if (!filters.subject) {
        filters.subject = subs[0].label;
      }
      setPrevSubject(filters.subject);
      setPrevLoType(filters.LOType);
      if (customTitle) {
        setTitle(customTitle);
      } else {
        setTitle(
          filters.masteryResults === "true"
            ? t("dashboard.masteryOverTimeFor") + " " + filters.subject
            : t("dashboard.gradeOverTimeFor") + " " + filters.subject
        );
      }
      DashboardService.getDashboardResults(
        filters.LOType,
        !filters.courses || filters.courses.length === 0
          ? filters.subject
          : null,
        "line",
        filters.terms,
        filters.courseLevels,
        filters.masteryResults,
        filters.categories,
        determineContext(finalizeFlag),
        filters.courses,
        filters.variety,
        filters.offerings,
        finalizeFlag
      ).then((res) => {
        const unroundedResults = res["results"]["ac_subject_results_over_time"];
        const roundedResults = unroundedResults.map((row, rowIndex) => {
          if (rowIndex === 0) {
            // If it's the first row (header row), keep it as it is.
            return row;
          } else {
            // For other rows, round numerical values to the hundredth.
            const roundedRow = row.map((value, index) => {
              return index === 0 || value === null
                ? value
                : Number(value.toFixed(2));
            });
            return roundedRow;
          }
        });
        if (isFacultyDash && assignmentData && section) {
          const term_in_question = section.term.name;
          const results_type =
            filters.masteryResults === "true" ? "mastered" : "score";
          const lo_type = filters.LOType ? filters.LOType : "CSLO";
          const av_results = getAverageResultsData(
            assignmentData,
            results_type,
            lo_type
          );
          const indexOfTerm = roundedResults[0].indexOf(term_in_question);
          const numTerms = roundedResults[0].length;
          let all_data_colors = {};

          // This for-loop goes line by line for the average data and creates
          // a row of graph data for it, and also creates the data colors array, ensuring that
          // the "Average" quality for an LO is a lighter variation of its respective LO color.
          for (const lo in av_results) {
            const data_line = getRowOfDataForAverageEntry(
              lo,
              numTerms,
              indexOfTerm,
              av_results
            );
            roundedResults.push(data_line);
            all_data_colors = setAverageDataColors(
              roundedResults,
              lo,
              all_data_colors
            );
          }
          setGraphColors(all_data_colors);
          setGraphData(roundedResults);
          setDataLoaded(true);
          if (setChartLoad) {
            setChartLoad(false);
          }
        } else {
          setGraphData(roundedResults);
          setDataLoaded(true);
          if (setChartLoad) {
            setChartLoad(false);
          }
        }
      });
    }
  };

  useEffect(() => {
    if (filters.subject) {
      if (trigger || chartLoad) {
        setDataLoaded(false);
        setGraphData(null);
        if (setChartTrigger) {
          setChartTrigger(false);
        }
        if (inAnalyze) {
          setTrigger(false);
        }
      }
      if (!trigger) {
        lineChartUpdate();
      }

      if (
        dataLoaded &&
        graphData &&
        graphData.length > 1 &&
        graphData[0].length > 1
      ) {
        setChart(
          c3.generate({
            bindto: "#chart",
            size: { height: 190 },
            data: {
              x: "terms",
              columns: graphData,
              type: "line",
              colors: graphColors, // verified that if this is an empty list it still works
            },
            padding: { bottom: 15 },
            axis: {
              x: { type: "category" },
              y: {
                label: {
                  text:
                    filters.masteryResults === "false"
                      ? t("dashboard.avgAssignmentGrade")
                      : t("dashboard.percentAchievedMastery"),
                  position: "outer-middle",
                },
                min: 5, // range includes padding, have to accomodate
                max: 100,
                tick: {
                  values: [0, 20, 40, 60, 80, 100],
                  format: function (d) {
                    return d + "%";
                  },
                },
              },
            },
            legend: { show: false },
            tooltip: {
              // Sort by name in the tooltip
              order: function (data1, data2) {
                return sort([data1, data2], (d) => d.name);
              },
            },
          })
        );
      }
    }
  }, [trigger, clear, graphData, assignmentData, chartLoad, filters.subject]);

  // This useEffect does everything that we just need to happen
  // on the re-rendering of the chart. These 3 things are:
  useEffect(() => {
    // 1: make the legend, re-rendering on every re-render of the chart
    if (chart && graphData && d3.select("[id^='chart']")) {
      const legendItems = graphData.slice(1).map((e) => e[0]);
      Legend({
        chart: chart,
        chartElementId: "chart",
        legendClassName: "time-chart-legend",
        legendItems: legendItems,
      });
      // 2: resize the chart on render to fit the full allowance given
      chart.resize();
      // 3: set the allowance of the bar chart to complement the width
      // of this line chart
      if (showBarChart) {
        setBarChartWidth(graphData.length > 9 ? "100%" : "50%");
      }
    }
  }, [chart]);

  return (
    <Flex.Item
      size={
        !showBarChart || (graphData && graphData.length > 9) ? "100%" : "50%"
      }
      padding="0 small"
    >
      {
        // note: graphData.length-1    = the number of ILOs/PLOs showing.
        //       graphData[0].length-1 = the number of terms showing.
        filters.subject && graphData && graphData[0].length > 1 ? (
          <View>
            <Heading as="h4" level="h4">
              {title}
            </Heading>
            <div id="chart" />
          </View>
        ) : dataLoaded && graphData && !trigger ? (
          <Alert variant="info">
            {t("notifications.assessments.noDataWithSubjectLine") +
              prevSubject +
              " " +
              prevLoType.replace("S", "") +
              "s"}
          </Alert>
        ) : (
          <Skeleton style={{ height: 64, borderRadius: 5, marginBottom: 10 }} />
        )
      }
    </Flex.Item>
  );
};

export default LineChart;
